import { cn, tv } from "tailwind-variants";
import {
	toHoverItemCssClasses,
	toHoverMediaCssClasses,
} from "#/_packages/umbrella/integrations/umbraco/utils/animation-utils.ts";
import type {
	AnimationScrollType,
	AspectRatio,
	BoxVariant,
	ImageMedia,
	ReadWidthLength,
} from "#/_packages/umbrella/types";
import { getAltTextFromString } from "#/_packages/umbrella/utils/image-utils.ts";

export type TestimonialAnimationScrollType = AnimationScrollType;
export type TestimonialBoxVariant = BoxVariant;
export type TestimonialReadWidthLength = ReadWidthLength;

export type TestimonialImage = ImageMedia;

export type TestimonialProps = {
	id?: string | null;
	quote?: string | null;
	source?: string | null;
	sourceDescription?: string | null;
	image?: TestimonialImage;
	aspectRatio?: AspectRatio;
	quotationMarks?: boolean;
	boxVariant?: BoxVariant;
	fullHeight?: boolean;
	readWidthToggle?: boolean;
	readWidthLength?: ReadWidthLength;
	animationHoverType?: string[];
	animationScrollType?: AnimationScrollType;
	isDrawer?: boolean;
	className?: string;
};

const testimonial = tv({
	slots: {
		root: "",
		block: "block__testimonial",
		quote: "testimonial-quote",
		sourceWrapper: "testimonial-source-wrapper",
		figure: "figure shrink-0 overflow-hidden",
		image: "testimonial-img object-fit-cover",
		sourceSymbol: "testimonial-source-symbol",
		sourceContent: "testimonial-source-content flex flex-col justify-between",
		source: "testimonial-source",
		sourceDescription: "testimonial-source-description",
		svgImage: "w-full",
	},
	variants: {
		boxVariant: {
			none: { root: "" },
			background: {
				root: "card card-background p-6",
			},
			outline: {
				root: "card card-outline p-6",
			},
			shadow: {
				root: "card card-shadow p-6",
			},
		},
		fullHeight: {
			true: { root: "h-full" },
			false: { root: "" },
		},
		alignmentSelfVertical: {
			stretch: { root: "" },
			start: { root: "self-start" },
			center: { root: "self-center" },
			end: { root: "self-end" },
		},
		animationScrollType: {
			none: { root: "" },
			"anim-view-fadeIn-moveUp": { root: "anim-view-fadeIn-moveUp" },
			"anim-view-fadeIn-moveDown": { root: "anim-view-fadeIn-moveDown" },
			"anim-view-fadeIn-moveLeft": { root: "anim-view-fadeIn-moveLeft" },
		},
		aspectRatio: {
			none: { svgImage: "" },
			"1:1": { svgImage: "aspect-square" },
			"4:3": { svgImage: "aspect-[4/3]" },
			"3:2": { svgImage: "aspect-[3/2]" },
			"16:9": { svgImage: "aspect-video" },
			"21:9": { svgImage: "aspect-[21/9]" },
		},
	},
	defaultVariants: {
		alignmentHorizontal: "start",
		boxVariant: "none",
		fullHeight: false,
		alignmentSelfVertical: "start",
		animationScrollType: "none",
		aspectRatio: "none",
	},
});

function hasText(value?: string | null) {
	return Boolean(value?.trim());
}

export function Testimonial({
	id,
	quote,
	source,
	sourceDescription,
	image,
	aspectRatio = "none",
	quotationMarks = false,
	boxVariant = "none",
	fullHeight = false,
	readWidthToggle = false,
	animationHoverType = [],
	animationScrollType = "none",
	className,
}: TestimonialProps) {
	if (!hasText(quote)) return null;

	const {
		root,
		block,
		quote: quoteClass,
		sourceWrapper,
		figure,
		image: imageClass,
		sourceSymbol,
		sourceContent,
		source: sourceClass,
		sourceDescription: sourceDescriptionClass,
		svgImage,
	} = testimonial({
		boxVariant,
		fullHeight,
		animationScrollType,
		aspectRatio,
	});

	const hoverItemClasses = toHoverItemCssClasses(animationHoverType);
	const hoverMediaClasses = toHoverMediaCssClasses(animationHoverType);
	const hasSource = hasText(source) || hasText(sourceDescription);
	const isSvg = image?.src?.toLowerCase().split("?")[0]?.endsWith(".svg");

	return (
		<div
			id={id ?? undefined}
			className={cn(
				root(),
				readWidthToggle && "read-width",
				hoverItemClasses,
				className,
			)}
		>
			<div id={id ? `${id}-block` : undefined} className={cn(block())}>
				<blockquote className="m-0">
					<p
						className={cn(
							quoteClass(),
							quotationMarks && "testimonial-quotation-marks",
						)}
					>
						{quote}
					</p>
				</blockquote>

				{hasSource ? (
					<div className={sourceWrapper()}>
						{image?.src ? (
							<figure className={cn(figure(), hoverMediaClasses)}>
								{isSvg ? (
									<img
										src={image.src}
										alt={getAltTextFromString(image.alt)}
										width={16}
										height={9}
										loading="lazy"
										className={cn(imageClass(), svgImage())}
									/>
								) : (
									<picture>
										<img
											src={image.src}
											alt={getAltTextFromString(image.alt)}
											loading="lazy"
											className={imageClass()}
										/>
									</picture>
								)}
							</figure>
						) : (
							<span className={sourceSymbol()} />
						)}
						<div className={sourceContent()}>
							{hasText(source) ? (
								<div className={sourceClass()}>{source}</div>
							) : null}
							{hasText(sourceDescription) ? (
								<div className={sourceDescriptionClass()}>
									{sourceDescription}
								</div>
							) : null}
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
}
